import { compareAsc, compareDesc } from "date-fns";
import { groupBy, keyBy, partition } from "lodash-es";

import {
  getGroupIdFromAccessId,
  getStudentIdFromAccessId,
  isGroupAccessId,
  isStudentAccessId,
} from "../../shared/access-id";

import {
  createAppSelector,
  selectAssessments,
  selectCourseOutcomes,
  selectCourses,
  selectCurrentUserId,
  selectOutcomeAssessments,
  selectPageGroupUsers,
  selectPageOutcomes,
  selectPages,
  selectSubmissions,
} from "./base";
import { selectPageOutcomesByPageId } from "./outcomes";
import { selectPageById } from "./page";
import { selectPageGroupUsersByPageId } from "./page-groups";
import { selectPagesForCurrentCourse } from "./section";
import {
  selectCourseUsersForCurrentUser,
  selectEnabledCourseUsers,
  selectEnabledCourseUsersByPageId,
} from "./section-user";
import { comparePagesByCategoryAndOrder, sortPagesWithAssessments } from "./sorting";
import { selectPagesForOutcome, selectPagesForTopic } from "./topics";
import { isIndividualSubmission } from "./type-helpers";
import type {
  FullGroupSubmissionType,
  FullIndividualSubmissionType,
  NestedAssessmentType,
  PageWithAssessmentsType,
  PagesWithOutcomeAssessmentAndSectionType,
  PagesWithOutcomeAssessmentType,
} from "./types";

import type {
  AssessmentType,
  GroupSubmissionType,
  IndividualSubmissionType,
  OutcomeAssessmentType,
  PageOutcomeType,
  PageType,
  SectionOutcomeType,
  SectionType,
  SectionUserType,
  SubmissionType,
} from "components/server-types";
import { isGroupPage, isIndividualPage, isSharedClassPage } from "utils/page-utils";

// TODO: There's extraneous data we're getting from the server, and we should fix it + clean up our selectors.
//  1. We may have allowed users to have submissions on group and individual submissions on the same page in the past.
//  We should ensure there is backend logic to validate that this can't happen, and delete any invalid data.
//  2. We are unfortunately returning submissions that are from instructors (this can happen when admins switch
//  between course user types student <-> instructor, etc.) as well as students, but we aren't filtering the
//  submission data to only show student data. There is a short-term fix in the selectSubmissionsForPage selector
//  to filter the submissions by the course_user's role type, which works for individual submissions, but not
//  group ones. We should come up for a fix for group submissions, filter them in the backend (or here), delete any
//  invalid data in our system, have a way to automatically delete this data on switch perhaps.
//  3. We should tighten up our assessment and submissions filters so that they reuse selectSubmissionsForPage when
//  they can, and resolve some of our many submission types.
//  4. There are two different ways to access assessment mode for instructors, the Assess button next to a page item
//  on the sidebar and the Assess button on the banner on the PageOverviewHeader. The assess button is calculated via
//  backend logic, while the other is calculated via frontend logic. We ensure that they are both in sync and are
//  using the same logic to calculate assessment mode.

/* *************************** Outcome Assessments **************************** */
export const selectOutcomeAssessmentByAssessmentId = createAppSelector(
  [(state, assessmentId: string) => assessmentId, selectOutcomeAssessments],
  (assessmentId, outcomeAssessments) => {
    return outcomeAssessments.filter((oa) => oa.assessment_id === assessmentId);
  },
);

export const selectOutcomeAssessmentsByPageId = createAppSelector(
  [selectPageOutcomesByPageId, selectOutcomeAssessments],
  (pageOutcomes, outcomeAssessments) => {
    const pageOutcomeIds = new Set(pageOutcomes.map((po) => po.id));
    return outcomeAssessments.filter((oa) => pageOutcomeIds.has(oa.page_outcome_id));
  },
);

/* *************************** Assessments **************************** */
export const selectAssessmentsByPageId = createAppSelector(
  [(state, pageId: string) => pageId, selectAssessments, (state) => state.submissions],
  (pageId, assessments, submissions): AssessmentType[] => {
    return assessments.filter((assessment) => {
      const submission = submissions[assessment.submission_id];
      return submission?.page_id === pageId;
    });
  },
);

export const selectHasAssessmentsForPageId = createAppSelector(
  [selectAssessmentsByPageId],
  (assessments) => assessments.length > 0,
);

export const selectAssessmentBySubmissionId = createAppSelector(
  [(state, submissionId: string) => submissionId, selectAssessments],
  (submissionId, assessments) => {
    return assessments.find((a) => a.submission_id === submissionId);
  },
);

export const selectUserAssessments = createAppSelector(
  [
    (state, userId: string | null) => userId,
    (state, userId: string | null, courseId?: string) => courseId,
    selectEnabledCourseUsers,
    selectSubmissions,
    selectAssessments,
    selectOutcomeAssessments,
    selectPageGroupUsers,
  ],
  (
    userId,
    courseId,
    courseUsers,
    submissions,
    assessments,
    outcomeAssessments,
    pageGroupUsers,
  ): NestedAssessmentType[] => {
    const filteredCourseUsers = courseUsers.filter(
      (cu) => (!userId || cu.user_id === userId) && (!courseId || cu.section_id === courseId),
    );
    const courseUserIds = new Set(filteredCourseUsers.map((cu) => cu.id));
    const filteredGroupUsers = pageGroupUsers.filter((pgu) =>
      courseUserIds.has(pgu.section_user_id),
    );

    const mySubmissions = submissions.filter((submission) =>
      isIndividualSubmission(submission)
        ? courseUserIds.has(submission.section_user_id)
        : filteredGroupUsers.some(
            (pgu) => pgu.page_group_id === submission.page_group_id && pgu.is_ready,
          ),
    );

    const myAssessments = assessments.filter((assessment) =>
      mySubmissions.some((s) => s.id === assessment.submission_id),
    );

    return myAssessments.map((assessment) => {
      const submission = mySubmissions.find((s) => s.id === assessment.submission_id);
      const outcomeAssessmentsForAssessment = outcomeAssessments.filter(
        (oa) => oa.assessment_id === assessment.id,
      );
      const nestedAssessment: NestedAssessmentType = {
        ...assessment,
        submission,
        outcome_assessments: outcomeAssessmentsForAssessment,
      };
      return nestedAssessment;
    });
  },
);

/* *************************** Submissions **************************** */
export const selectSubmissionsByPageId = createAppSelector(
  [
    (state, pageId: string) => pageId,
    selectPageById,
    selectSubmissions,
    selectEnabledCourseUsersByPageId,
    selectPageGroupUsersByPageId,
  ],
  (pageId, page, submissions, enabledCourseUsers, pageGroupUsers) =>
    submissions.filter((submission) => {
      if (
        submission.page_id !== pageId ||
        (isGroupPage(page) && isIndividualSubmission(submission)) ||
        (isIndividualPage(page) && !isIndividualSubmission(submission))
      ) {
        return false;
      }

      if (isIndividualSubmission(submission)) {
        // If the submission is for a user that doesn't exist or is disabled, skip it.
        return enabledCourseUsers.some((cu) => cu.id === submission.section_user_id);
      }

      return pageGroupUsers.some(
        (pgu) => pgu.page_group_id === submission.page_group_id && pgu.is_ready,
      );
    }),
);

export const selectHasSubmissionsForPageId = createAppSelector(
  [selectSubmissionsByPageId],
  (submissions) => submissions.length > 0,
);

export const selectSortedSubmissionsByPageId = createAppSelector(
  [selectSubmissionsByPageId],
  (submissions) => submissions.toSorted((a, b) => compareDesc(a.created_at, b.created_at)),
);

/**
 * Given a submission ID, returns the official submission for the same student/group.
 * Used to determine if the current submission is "new" (created after an assessed submission).
 */
export const selectOfficialSubmissionBySubmissionId = createAppSelector(
  [(state) => state.submissions, (_state, submissionId: string | undefined | null) => submissionId],
  (submissionsMap, submissionId) => {
    if (!submissionId) return undefined;

    const submission = submissionsMap[submissionId];
    if (!submission) return undefined;

    // Find all submissions for the same access (course_user or page_group)
    const allSubmissions = Object.values(submissionsMap);
    const sameAccessSubmissions = allSubmissions.filter((s) =>
      submission.section_user_id
        ? s.section_user_id === submission.section_user_id && s.page_id === submission.page_id
        : s.page_group_id === submission.page_group_id && s.page_id === submission.page_id,
    );

    // Return the official submission (the one with official_at set)
    return sameAccessSubmissions.find((s) => s.official_at);
  },
);

export const selectFullSubmissionsByPageId = createAppSelector(
  [
    selectSubmissionsByPageId,
    selectAssessmentsByPageId,
    selectOutcomeAssessmentsByPageId,
    (state) => state.section_users,
  ],
  (
    submissions,
    assessments,
    outcome_assessments,
    courseUsersMap,
  ): FullIndividualSubmissionType[] | FullGroupSubmissionType[] => {
    return submissions.map((submission) => {
      const assessment = assessments.find((a) => a.submission_id === submission.id);
      const outcomeAssessments = outcome_assessments.filter(
        (oa) => oa.assessment_id === assessment?.id,
      );
      if (isIndividualSubmission(submission)) {
        const course_user = courseUsersMap[submission.section_user_id];
        return {
          ...submission,
          course_user,
          assessment,
          outcome_assessments: outcomeAssessments,
        } as FullIndividualSubmissionType;
      }
      return {
        ...submission,
        assessment,
        outcome_assessments: outcomeAssessments,
      } as FullGroupSubmissionType;
    }) as FullIndividualSubmissionType[] | FullGroupSubmissionType[];
  },
);

export const selectUserSubmissionsByPageId = createAppSelector(
  [
    (state, pageId: string, selectedAccessId?: string) => selectedAccessId,
    selectPageById,
    selectCurrentUserId,
    selectSubmissionsByPageId,
    selectEnabledCourseUsersByPageId,
    selectPageGroupUsersByPageId,
    (state) => state.page_groups,
  ],
  (
    selectedAccessId,
    page,
    currentUserId,
    submissions,
    courseUsers,
    pageGroupUsers,
    pageGroupMap,
  ): IndividualSubmissionType[] | GroupSubmissionType[] => {
    if (
      !page ||
      isSharedClassPage(page) ||
      (selectedAccessId && isIndividualPage(page) && !isStudentAccessId(selectedAccessId)) ||
      (selectedAccessId && isGroupPage(page) && !isGroupAccessId(selectedAccessId))
    ) {
      return [];
    }

    if (isIndividualPage(page)) {
      const userId = selectedAccessId ? getStudentIdFromAccessId(selectedAccessId) : currentUserId;
      const courseUser = courseUsers.find((cu) => cu.user_id === userId);
      if (!courseUser) {
        return [];
      }

      return submissions.filter(
        (submission) => submission.section_user_id === courseUser.id,
      ) as IndividualSubmissionType[];
    }

    let groupId: string;
    if (selectedAccessId) {
      groupId = getGroupIdFromAccessId(selectedAccessId);
    } else {
      const courseUser = courseUsers.find((cu) => cu.user_id === currentUserId);
      const pageGroupUser = pageGroupUsers.find((pgu) => {
        const group = pageGroupMap[pgu.page_group_id];
        return pgu.section_user_id === courseUser?.id && pgu.is_ready && group?.page_id === page.id;
      });
      groupId = pageGroupUser?.page_group_id;
    }

    return submissions.filter(
      (submission) => submission.page_group_id === groupId,
    ) as GroupSubmissionType[];
  },
);

export const selectUserFullSubmissionsByPageId = createAppSelector(
  [
    selectPageById,
    selectUserSubmissionsByPageId,
    selectAssessmentsByPageId,
    selectEnabledCourseUsersByPageId,
    selectOutcomeAssessmentsByPageId,
  ],
  (
    page,
    submissions,
    assessments,
    courseUsers,
    outcomeAssessments,
  ): FullIndividualSubmissionType[] | FullGroupSubmissionType[] => {
    const courseUsersMap = keyBy(courseUsers, (cu) => cu.id);
    if (isIndividualPage(page)) {
      return (submissions as IndividualSubmissionType[]).map((submission) => {
        const course_user = courseUsersMap[submission.section_user_id];
        const assessment = assessments.find((a) => a.submission_id === submission.id);
        const outcome_assessments = outcomeAssessments.filter(
          (oa) => oa.assessment_id === assessment?.id,
        );
        return {
          ...submission,
          course_user,
          assessment,
          outcome_assessments,
        };
      }) as FullIndividualSubmissionType[];
    }
    return (submissions as GroupSubmissionType[]).map((submission) => {
      const assessment = assessments.find((a) => a.submission_id === submission.id);
      const outcome_assessments = outcomeAssessments.filter(
        (oa) => oa.assessment_id === assessment?.id,
      );
      return {
        ...submission,
        assessment,
        outcome_assessments,
      };
    }) as FullGroupSubmissionType[];
  },
);

export const selectUserOfficialSubmissionByPageId = createAppSelector(
  [selectUserSubmissionsByPageId],
  (submissions) => {
    return submissions.find((submission) => submission.official_at);
  },
);

export const selectAssessedSubmissionsByTopicId = createAppSelector(
  [selectPagesForTopic, selectAssessments, selectSubmissions],
  (pages, assessments, submissions) => {
    const pageIds = pages.map((page) => page.id);
    return submissions
      .filter((s) => pageIds.includes(s.page_id))
      .filter((s) => assessments.find((a) => a.submission_id === s.id));
  },
);

export const selectAssessedSubmissionsByPageId = createAppSelector(
  [selectAssessmentsByPageId, selectSubmissions],
  (assessments, submissions) => {
    return submissions.filter((s) => assessments.find((a) => a.submission_id === s.id));
  },
);

export const selectSortedUserFullSubmissionsByPageId = createAppSelector(
  [selectUserFullSubmissionsByPageId],
  (submissions) =>
    submissions.toSorted((a: SubmissionType, b: SubmissionType) =>
      compareAsc(a.created_at, b.created_at),
    ),
);

export const selectDefaultSubmissionForUser = createAppSelector(
  [
    selectUserSubmissionsByPageId,
    (state, pageId: string, selectedAccessId?: string, isAssessing?: boolean) => isAssessing,
  ],
  (submissions, isAssessing): SubmissionType => {
    // TODO: move this logic to helper function
    let sortedSubmissions: SubmissionType[];

    // If the user is assessing, the default submission should be the latest submission.
    // If not, the default submission should be the official submission if it exists,
    // otherwise the latest submission.
    if (isAssessing) {
      sortedSubmissions = submissions.toSorted((a, b) => compareDesc(a.created_at, b.created_at));
    } else {
      sortedSubmissions = submissions.toSorted((a, b) =>
        a.official_at ? -1 : b.official_at ? 1 : compareDesc(a.created_at, b.created_at),
      );
    }

    return sortedSubmissions[0];
  },
);

export const selectOfficialFullSubmissionsByPageId = createAppSelector(
  [selectFullSubmissionsByPageId],
  (submissions) => submissions.filter((submission) => submission.official_at),
);

/* *************************** Pages **************************** */
export const selectPagesWithAssessmentsForUserId = createAppSelector(
  [
    (state, userId: string | null, courseId: string) =>
      selectUserAssessments(state, userId, courseId),
    selectPages,
    (s) => s.activities,
    (s) => s.topics,
  ],
  (currentUserAssessments, pages, activityMap, topicMap): PageWithAssessmentsType[] => {
    const assessmentPages = pages.flatMap((p) => {
      const pageAssessments = currentUserAssessments.filter((ca) => ca.submission.page_id === p.id);
      if (!pageAssessments.length) {
        return [];
      }

      const [officialPageAssessments, nonOfficialPageAssessments] = partition(
        pageAssessments,
        (a) => !!a.submission.official_at,
      );

      const activity = p.activity_id ? activityMap[p.activity_id] : null;
      const topic = activity ? topicMap[activity.topic_id] : null;

      return [
        {
          ...p,
          topic,
          assessments: officialPageAssessments,
          nonOfficialAssessments: nonOfficialPageAssessments,
        },
      ];
    });
    return assessmentPages.sort(comparePagesByCategoryAndOrder);
  },
);

/**
 * This helper function creates the final page assessment objects, optionally including course title.
 */
const _getPagesWithAssessmentsForCourseOutcomeAndUser = (
  courseOutcomeId: string,
  currentUserAssessments: NestedAssessmentType[],
  pageOutcomesForCourseOutcome: PageOutcomeType[],
  pagesForOutcome: PageType[],
  options: {
    excludeUnassessedPages?: boolean;
    courseTitle?: string;
  } = {},
): PagesWithOutcomeAssessmentType[] | PagesWithOutcomeAssessmentAndSectionType[] => {
  const { excludeUnassessedPages = false, courseTitle = "" } = options;
  const assessmentsByPageId = groupBy(
    currentUserAssessments,
    (assessment) => assessment.submission.page_id,
  );

  const pagesWithAssessments = pagesForOutcome
    .filter((page) => {
      if (!excludeUnassessedPages) return true;
      const pageAssessments = assessmentsByPageId[page.id] || [];
      return !!pageAssessments.find((a) => a.submission.official_at);
    })
    .map((page) => {
      const pageAssessments = assessmentsByPageId[page.id] || [];
      const officialPageAssessment = pageAssessments.find((a) => a.submission.official_at);
      const pageOutcomeForCourseOutcome = pageOutcomesForCourseOutcome.find(
        (po) => po.page_id === page.id,
      );
      const outcomeAssessment = officialPageAssessment?.outcome_assessments.find(
        (oa) => oa.page_outcome_id === pageOutcomeForCourseOutcome?.id,
      );

      const basePage = {
        ...page,
        assessment: officialPageAssessment,
        outcomeAssessment,
        nonOfficialAssessments: pageAssessments,
      };

      if (courseTitle) {
        return {
          ...basePage,
          course_title: courseTitle,
        } as PagesWithOutcomeAssessmentAndSectionType;
      }

      return basePage;
    });

  return pagesWithAssessments;
};

export const selectPagesWithAssessmentsForCourseOutcomeAndUser = createAppSelector(
  [
    (state, courseOutcomeId: string) => courseOutcomeId,
    (state, courseOutcomeId: string, userId: string, courseId: string) =>
      selectUserAssessments(state, userId, courseId),
    selectPageOutcomes,
    selectPagesForOutcome,
  ],
  (
    courseOutcomeId,
    currentUserAssessments,
    pageOutcomes,
    pagesForOutcome,
  ): PagesWithOutcomeAssessmentType[] => {
    const pageOutcomesForCourseOutcome = pageOutcomes.filter(
      (po) => po.section_outcome_id === courseOutcomeId,
    );

    const pagesWithAssessments = _getPagesWithAssessmentsForCourseOutcomeAndUser(
      courseOutcomeId,
      currentUserAssessments,
      pageOutcomesForCourseOutcome,
      pagesForOutcome,
      { excludeUnassessedPages: false },
    );

    return sortPagesWithAssessments(pagesWithAssessments);
  },
);

export const selectPagesWithAssessments = createAppSelector(
  [
    selectPagesForCurrentCourse,
    selectSubmissions,
    selectAssessments,
    selectOutcomeAssessments,
    (s) => s.activities,
    (s) => s.topics,
  ],
  (
    pages,
    submissions,
    assessments,
    outcomeAssessments,
    activityMap,
    topicMap,
  ): PageWithAssessmentsType[] => {
    const scoreablePages = pages.filter((p) => p.released_at);
    const pagesWithAssessments = scoreablePages.map((page) => {
      const pageSubmissions = submissions.filter((s) => s.page_id === page.id);
      const officialPageSubmissions = pageSubmissions.filter((s) => s.official_at);
      const pageAssessments = assessments.filter((a) =>
        officialPageSubmissions.some((submission) => a.submission_id === submission?.id),
      );
      const nonOfficialAssessments = pageAssessments.filter(
        (a) => !officialPageSubmissions.some((submission) => a.submission_id === submission?.id),
      );
      const nestedPageAssessments: NestedAssessmentType[] = pageAssessments.map((assessment) => ({
        ...assessment,
        submission: officialPageSubmissions.find((s) => s.id === assessment.submission_id),
        outcome_assessments: outcomeAssessments.filter((oa) => oa.assessment_id === assessment.id),
      }));
      const nestedNonOfficialAssessments: NestedAssessmentType[] = nonOfficialAssessments.map(
        (assessment) => ({
          ...assessment,
          submission: pageSubmissions.find((s) => s.id === assessment.submission_id),
          outcome_assessments: outcomeAssessments.filter(
            (oa) => oa.assessment_id === assessment.id,
          ),
        }),
      );
      const activity = page.activity_id ? activityMap[page.activity_id] : null;
      const topic = activity ? topicMap[activity.topic_id] : null;

      const ret: PageWithAssessmentsType = {
        ...page,
        topic,
        assessments: nestedPageAssessments,
        nonOfficialAssessments: nestedNonOfficialAssessments,
      };
      return ret;
    });
    return pagesWithAssessments.sort(comparePagesByCategoryAndOrder);
  },
);

export const selectSubmissionById = createAppSelector(
  [(state) => state.submissions, (state, submissionId: string | null) => submissionId],
  (submissionsObj, submissionId): SubmissionType | undefined =>
    submissionId ? submissionsObj[submissionId] : undefined,
);

export const selectFullSubmissionById = createAppSelector(
  [
    selectSubmissionById,
    selectOutcomeAssessments,
    (state) => state.assessments,
    (state) => state.section_users,
  ],
  (
    submission,
    outcomeAssessments,
    assessmentsObj,
    courseUsersObj,
  ): FullIndividualSubmissionType | FullGroupSubmissionType | undefined => {
    if (!submission) {
      return undefined;
    }

    const assessment = Object.values(assessmentsObj).find((a) => a.submission_id === submission.id);
    const outcome_assessments = outcomeAssessments.filter(
      (oa) => oa.assessment_id === assessment?.id,
    );
    if (isIndividualSubmission(submission)) {
      const course_user = courseUsersObj[submission.section_user_id];
      return {
        ...submission,
        course_user,
        assessment,
        outcome_assessments,
      } as FullIndividualSubmissionType;
    }
    return {
      ...submission,
      assessment,
      outcome_assessments,
    } as FullGroupSubmissionType;
  },
);

/**
 * This selector finds all course outcomes relevant to the current user across all their courses.
 */
export const selectCourseOutcomesForUser = createAppSelector(
  [selectCourseOutcomes, selectCourseUsersForCurrentUser],
  (
    courseOutcomes,
    courseUsersForCurrentUser,
  ): Array<{ courseOutcome: SectionOutcomeType; courseUser: SectionUserType }> => {
    const courseOutcomesByCourseId = groupBy(
      courseOutcomes,
      (courseOutcome) => courseOutcome.section_id,
    );

    return courseUsersForCurrentUser.flatMap((courseUser) => {
      const courseOutcomesForCourse = courseOutcomesByCourseId[courseUser.section_id] || [];
      return courseOutcomesForCourse.map((courseOutcome) => ({
        courseOutcome,
        courseUser,
      }));
    });
  },
);

/**
 * This selector filters all course outcomes relevant to the current user to those that
 * match the specified outcome ID.
 */
export const selectCourseOutcomesForOutcomeAndUser = createAppSelector(
  [(state, outcomeId: string) => outcomeId, selectCourseOutcomesForUser],
  (
    outcomeId,
    allCourseOutcomesForUser,
  ): Array<{ courseOutcome: SectionOutcomeType; courseUser: SectionUserType }> => {
    return allCourseOutcomesForUser.filter((item) => item.courseOutcome.outcome_id === outcomeId);
  },
);

/**
 * This helper function pre-computes lookup maps to avoid poor filtering performance in the main selector logic.
 */
const _createLookupMaps = (
  allSubmissions: SubmissionType[],
  allAssessments: AssessmentType[],
  allOutcomeAssessments: OutcomeAssessmentType[],
  allPageOutcomes: PageOutcomeType[],
  allPages: PageType[],
  allCourses: SectionType[],
) => {
  const submissionsByCourseUserId = groupBy(
    allSubmissions,
    (submission) => submission.section_user_id,
  );

  const assessmentsBySubmissionId = keyBy(allAssessments, (assessment) => assessment.submission_id);
  const outcomeAssessmentsByAssessmentId = groupBy(allOutcomeAssessments, (oa) => oa.assessment_id);
  const pageOutcomesByCourseOutcome = groupBy(allPageOutcomes, (po) => po.section_outcome_id);
  const pagesByCourseId = groupBy(allPages, (page) => page.section_id);
  const coursesById = keyBy(allCourses, (course) => course.id);

  return {
    submissionsByCourseUserId,
    assessmentsBySubmissionId,
    outcomeAssessmentsByAssessmentId,
    pageOutcomesByCourseOutcome,
    pagesByCourseId,
    coursesById,
  };
};

/**
 * This selector aggregates a user's assessment data from multiple courses for pages that are
 * linked to the specified outcome ID.
 */
export const selectPagesWithAssessmentsForOutcomeAndUser = createAppSelector(
  [
    (state, outcomeId: string) => outcomeId,
    (state, outcomeId: string) => selectCourseOutcomesForOutcomeAndUser(state, outcomeId),
    selectCourses,
    selectPages,
    selectAssessments,
    selectPageOutcomes,
    selectSubmissions,
    selectOutcomeAssessments,
  ],
  (
    outcomeId,
    courseOutcomesForUser,
    allCourses,
    allPages,
    allAssessments,
    allPageOutcomes,
    allSubmissions,
    allOutcomeAssessments,
  ): PagesWithOutcomeAssessmentAndSectionType[] => {
    if (courseOutcomesForUser.length === 0) return [];

    const {
      submissionsByCourseUserId,
      assessmentsBySubmissionId,
      outcomeAssessmentsByAssessmentId,
      pageOutcomesByCourseOutcome,
      pagesByCourseId,
      coursesById,
    } = _createLookupMaps(
      allSubmissions,
      allAssessments,
      allOutcomeAssessments,
      allPageOutcomes,
      allPages,
      allCourses,
    );

    const pagesWithAssessments = courseOutcomesForUser.flatMap(({ courseOutcome, courseUser }) => {
      const course_id = courseUser.section_id;
      const userSubmissions = submissionsByCourseUserId[courseUser.id] || [];
      const coursePages = pagesByCourseId[course_id] || [];
      const pageOutcomesForCourseOutcome = pageOutcomesByCourseOutcome[courseOutcome.id] || [];
      const pageIdsForOutcome = new Set(pageOutcomesForCourseOutcome.map((po) => po.page_id));
      const pagesForOutcome = coursePages.filter((page) => pageIdsForOutcome.has(page.id));
      const course = coursesById[course_id];
      const currentUserAssessments: NestedAssessmentType[] = userSubmissions.flatMap(
        (submission) => {
          const assessment = assessmentsBySubmissionId[submission.id];
          if (!assessment) return [];

          const outcomeAssessments = outcomeAssessmentsByAssessmentId[assessment.id] || [];
          return [
            {
              ...assessment,
              submission,
              outcome_assessments: outcomeAssessments,
            },
          ];
        },
      );

      const pages = _getPagesWithAssessmentsForCourseOutcomeAndUser(
        courseOutcome.id,
        currentUserAssessments,
        pageOutcomesForCourseOutcome,
        pagesForOutcome,
        { excludeUnassessedPages: true, courseTitle: course?.title || "" },
      );

      return pages as PagesWithOutcomeAssessmentAndSectionType[];
    });

    return sortPagesWithAssessments(pagesWithAssessments);
  },
);

export const selectAssessedPageOutcomesByCourseOutcome = createAppSelector(
  [
    (state, courseOutcomeId: string) => courseOutcomeId,
    selectPageOutcomes,
    selectOutcomeAssessments,
  ],
  (courseOutcomeId, pageOutcomes, outcomeAssessments) => {
    const pageOutcomesForCourseOutcome = pageOutcomes.filter(
      (po) => po.section_outcome_id === courseOutcomeId,
    );

    return pageOutcomesForCourseOutcome.filter((po) => {
      return outcomeAssessments.some((oa) => oa.page_outcome_id === po.id);
    });
  },
);
